import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LuPrinter } from "react-icons/lu";
import { CustomInput } from "../../components/CustomInput";
import CustomSearchSelect from "../../components/CustomSearchSelect";
import RelationInput from "../../components/RelationInput";
import TablePagination from "../../components/TablePagination";
import useFetch from "../../hooks/useFetch";
import { getInvoiceOrders } from "../../services/order";
import { formatDate, formatMoney } from "../../utils/format";
import { errorMsg } from "../../utils/toast-msg";
import { ORDER_STATUS_OPTIONS } from "./orderStatuses";
import InvoiceSummaryDialog from "./InvoiceSummaryDialog";
import dashboardStyles from "../../styles/dashboard.module.css";
import styles from "../../styles/page.module.css";
import rowStyles from "../../styles/orderRows.module.css";

// Bulk invoice print: pick a customer, narrow their orders (date range, status, search), tick the orders and
// print all their invoices together - each invoice starts on a new A4 page. The picked orders stay picked while
// paging and filtering.
const InvoicePrintPage = () => {
  const [customerId, setCustomerId] = useState("");
  const [range, setRange] = useState({ from: "", to: "" });
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [picked, setPicked] = useState(() => new Map()); // id -> code
  const [summaryIds, setSummaryIds] = useState(null); // the orders whose summary is shown before the invoice is generated
  const [separate, setSeparate] = useState(false); // false = ONE invoice for all the picked orders, true = one per page

  useEffect(() => {
    const timer = setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // another customer starts a new selection
  useEffect(() => {
    setPicked(new Map());
    setPage(1);
  }, [customerId]);

  const { data, loading } = useFetch(
    () =>
      customerId
        ? getInvoiceOrders({ b2bUserId: customerId, fromDate: range.from, toDate: range.to, status, searchString: query, page, limit })
        : Promise.resolve({ data: [], meta: { total: 0 } }),
    [customerId, range.from, range.to, status, query, page, limit]
  );

  const orders = data?.data || [];
  const total = data?.meta?.total || 0;
  const allTicked = orders.length > 0 && orders.every((order) => picked.has(order.id));

  const toggle = (order) =>
    setPicked((current) => {
      const next = new Map(current);
      if (next.has(order.id)) next.delete(order.id);
      else next.set(order.id, order.code);
      return next;
    });
  const toggleAll = () =>
    setPicked((current) => {
      const next = new Map(current);
      orders.forEach((order) => (allTicked ? next.delete(order.id) : next.set(order.id, order.code)));
      return next;
    });

  // opens the summary; the invoice is generated only when it is confirmed there
  const print = () => {
    if (!picked.size) return errorMsg("Select at least one order");
    setSummaryIds([...picked.keys()]);
  };

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Print Invoices</h1>
          <p className={styles.pageSub}>Pick a customer, tick their orders and print them on one invoice - every order listed, with the total of all</p>
        </div>
        <div className={styles.pageActions}>
          <Link to="/dashboard/invoice-history" className={`${dashboardStyles.btn} ${dashboardStyles.btnOutline}`} style={{ textDecoration: "none" }}>
            Invoice History
          </Link>
          <button type="button" className={`${dashboardStyles.btn} ${dashboardStyles.btnPrimary}`} onClick={print} disabled={picked.size === 0}>
            <LuPrinter />
            Generate & Print{picked.size ? ` (${picked.size})` : ""}
          </button>
        </div>
      </div>

      <section className={dashboardStyles.card}>
        <div className={styles.toolbar} style={{ alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
          <div style={{ minWidth: 260, flex: "1 1 260px" }}>
            <label className="form-label" htmlFor="invoice-customer">
              Customer
            </label>
            <RelationInput id="invoice-customer" slug="agent-lookup" value={customerId} placeholder="Search customer by code or name" onChange={(id) => setCustomerId(id || "")} />
          </div>
          <div className={styles.dateFilter}>
            <label className="form-label" htmlFor="invoice-from">
              From
            </label>
            <CustomInput id="invoice-from" type="date" value={range.from} onChange={(event) => { setRange({ ...range, from: event.target.value }); setPage(1); }} sx={{ "& .MuiInputBase-root": { height: "38px" } }} />
          </div>
          <div className={styles.dateFilter}>
            <label className="form-label" htmlFor="invoice-to">
              To
            </label>
            <CustomInput id="invoice-to" type="date" value={range.to} onChange={(event) => { setRange({ ...range, to: event.target.value }); setPage(1); }} sx={{ "& .MuiInputBase-root": { height: "38px" } }} />
          </div>
          <div className={styles.dateFilter} style={{ minWidth: 200 }}>
            <label className="form-label" htmlFor="invoice-status">
              Status
            </label>
            <CustomSearchSelect
              id="invoice-status"
              name="status"
              height="38px"
              placeholder="All"
              options={ORDER_STATUS_OPTIONS.map((value) => ({ value, label: value }))}
              value={status}
              onChange={(next) => { setStatus(next || ""); setPage(1); }}
            />
          </div>
          <div className={styles.dateFilter}>
            <label className="form-label" htmlFor="invoice-search">
              Search
            </label>
            <CustomInput id="invoice-search" placeholder="Order no / product" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ "& .MuiInputBase-root": { height: "38px" } }} />
          </div>
        </div>

        {picked.size > 0 && (
          <div className={rowStyles.bulkBar} role="toolbar" aria-label="Selected orders" style={{ marginTop: 12 }}>
            <span className={rowStyles.bulkCount}>{picked.size} selected</span>
            <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={separate} onChange={(event) => setSeparate(event.target.checked)} />
              Print each order as its own invoice (a new page each)
            </label>
            <button type="button" className={`${dashboardStyles.btn} ${dashboardStyles.btnOutline}`} onClick={() => setPicked(new Map())}>
              Clear
            </button>
          </div>
        )}

        <div className={dashboardStyles.tableWrap} style={{ opacity: loading ? 0.5 : 1, marginTop: 12 }}>
          <table className={dashboardStyles.table} style={{ minWidth: 760 }}>
            <thead>
              <tr>
                <th style={{ width: 34 }}>
                  <input type="checkbox" checked={allTicked} onChange={toggleAll} disabled={!orders.length} aria-label="Select all orders on this page" />
                </th>
                <th>Order</th>
                <th>Date</th>
                <th>Product</th>
                <th style={{ textAlign: "right" }}>Total</th>
                <th style={{ textAlign: "right" }}>Paid</th>
                <th>Payment</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {!customerId ? (
                <tr>
                  <td colSpan={8} className={dashboardStyles.emptyRow}>
                    Select a customer to see their orders
                  </td>
                </tr>
              ) : orders.length === 0 && !loading ? (
                <tr>
                  <td colSpan={8} className={dashboardStyles.emptyRow}>
                    No orders found
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr key={order.id} className={picked.has(order.id) ? rowStyles.rowSelected : undefined}>
                    <td>
                      <input type="checkbox" checked={picked.has(order.id)} onChange={() => toggle(order)} aria-label={`Select ${order.code}`} />
                    </td>
                    <td>
                      <Link to={`/dashboard/order/${order.id}`} style={{ color: "var(--color-primary)", fontWeight: 600, textDecoration: "none" }}>
                        {order.code}
                      </Link>
                    </td>
                    <td>{formatDate(order.orderDate, "DD/MMM/YYYY")}</td>
                    <td>{order.productTitle || "-"}</td>
                    <td style={{ textAlign: "right" }}>{formatMoney(+order.totalAmount || 0)}</td>
                    <td style={{ textAlign: "right" }}>{formatMoney(+order.paidAmount || 0)}</td>
                    <td>{String(order.paymentStatus || "").replace(/_/g, " ")}</td>
                    <td>{order.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {customerId && (
          <TablePagination
            page={page}
            limit={limit}
            total={total}
            onPageChange={setPage}
            onLimitChange={(value) => {
              setLimit(value);
              setPage(1);
            }}
          />
        )}
      </section>
      <InvoiceSummaryDialog orderIds={summaryIds} separate={separate} onClose={() => setSummaryIds(null)} onDone={() => setPicked(new Map())} />
    </div>
  );
};

export default InvoicePrintPage;
